/**
 * ProssimeChiusure.jsx
 * Il Diario di Bordo - SaaS
 * 
 * Card per dashboard che mostra le prossime chiusure
 * Max 5 chiusure, alert urgente se ≤3 giorni
 */

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { calendarioApi } from '../../services/api'
import { Calendar, AlertTriangle, ChevronRight, Loader2 } from 'lucide-react'

export default function ProssimeChiusure() {
  const [chiusure, setChiusure] = useState([])
  const [loading, setLoading] = useState(true)
  
  useEffect(() => {
    loadChiusure()
  }, [])
  
  const loadChiusure = async () => {
    try {
      setLoading(true)
      const res = await calendarioApi.getProssimeChiusure(30)
      setChiusure(res.data?.data || [])
    } catch (error) {
      console.error('Errore caricamento chiusure:', error)
      setChiusure([])
    } finally {
      setLoading(false)
    }
  }
  
  if (loading) {
    return (
      <div className="card p-4">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      </div>
    )
  }
  
  if (chiusure.length === 0) {
    return null // Non mostrare nulla se non ci sono chiusure
  }
  
  // Prendi solo le prime 5
  const chiusureToShow = chiusure.slice(0, 5)
  const hasUrgent = chiusureToShow.some(c => c.giorniMancanti <= 3)
  
  return (
    <div className={`card overflow-hidden ${hasUrgent ? 'border-orange-300 dark:border-orange-700' : ''}`}>
      {/* Header */}
      <div className={`px-4 py-3 flex items-center justify-between ${
        hasUrgent 
          ? 'bg-orange-50 dark:bg-orange-900/20' 
          : 'bg-gray-50 dark:bg-gray-800'
      }`}>
        <div className="flex items-center gap-2">
          <Calendar className={`h-5 w-5 ${hasUrgent ? 'text-orange-600' : 'text-gray-600'}`} />
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Prossime Chiusure
          </h3>
          {hasUrgent && (
            <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full">
              Imminente
            </span>
          )}
        </div>
        <span className="text-xs text-gray-500">{chiusure.length} nei prossimi 30gg</span>
      </div>
      
      {/* Lista chiusure */}
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {chiusureToShow.map((chiusura, idx) => (
          <ChiusuraItem key={idx} chiusura={chiusura} />
        ))}
      </div>
      
      {/* Footer */}
      {chiusure.length > 5 && (
        <Link 
          to="/settings?tab=calendario"
          className="block px-4 py-3 text-sm text-center text-primary-600 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
        >
          Vedi tutte ({chiusure.length})
          <ChevronRight className="h-4 w-4 inline ml-1" />
        </Link>
      )}
    </div>
  )
}

function ChiusuraItem({ chiusura }) {
  const isUrgent = chiusura.giorniMancanti <= 3
  const isToday = chiusura.giorniMancanti === 0
  const isTomorrow = chiusura.giorniMancanti === 1
  
  // Colore badge basato su tipologia
  const getBadgeStyle = () => {
    switch (chiusura.tipologia) {
      case 'festivita':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
      case 'santo_patrono':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
      case 'chiusura_straordinaria':
        return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
    }
  }
  
  // Testo giorni mancanti
  const getGiorniText = () => {
    if (isToday) return 'Oggi'
    if (isTomorrow) return 'Domani'
    return `Tra ${chiusura.giorniMancanti} giorni`
  }
  
  return (
    <div className={`px-4 py-3 flex items-center justify-between ${
      isUrgent ? 'bg-orange-50/50 dark:bg-orange-900/10' : ''
    }`}>
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-xl flex-shrink-0">{chiusura.emoji || '📅'}</span>
        <div className="min-w-0">
          <p className="font-medium text-gray-900 dark:text-white truncate">
            {chiusura.motivo}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {chiusura.dataFormattata}
            {chiusura.parziale && (
              <span className="ml-2">
                {chiusura.parziale === 'mattina' ? '🌅 Solo mattina' : '🌆 Solo pomeriggio'}
              </span>
            )}
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-2 flex-shrink-0">
        {isUrgent && (
          <AlertTriangle className="h-4 w-4 text-orange-500" />
        )}
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
          isUrgent 
            ? 'bg-orange-500 text-white' 
            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
        }`}>
          {getGiorniText()}
        </span>
      </div>
    </div>
  )
}
